import { useState, useEffect, useCallback, useRef } from 'react';
import { emailAPI } from '../../../services/api';

interface EmailTemplate {
  id: number;
  trigger: string;
  trigger_display: string;
  subject: string;
  body_html: string;
  body_text: string;
  is_active: boolean;
  from_name: string;
  reply_to: string;
  updated_at: string;
}

interface EmailLog {
  id: number;
  template_trigger: string;
  to_email: string;
  subject: string;
  status: string;
  sent_at: string;
  error_message?: string;
}

interface TriggerVariables {
  [trigger: string]: string[];
}

interface TemplateForm {
  subject: string;
  body_html: string;
  body_text: string;
  is_active: boolean;
  from_name: string;
  reply_to: string;
}

const initialForm: TemplateForm = {
  subject: '',
  body_html: '',
  body_text: '',
  is_active: true,
  from_name: '',
  reply_to: '',
};

export function useEmailTemplates() {
  // Templates
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  // Selected template for editing
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [showEditor, setShowEditor] = useState(false);

  // Form state
  const [form, setForm] = useState<TemplateForm>(initialForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  // Preview
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Test email
  const [testEmail, setTestEmail] = useState('');
  const [showTestModal, setShowTestModal] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testSuccess, setTestSuccess] = useState('');
  const [testError, setTestError] = useState('');

  // Logs
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [showLogs, setShowLogs] = useState(false);

  // Trigger variables
  const [triggerVariables, setTriggerVariables] = useState<TriggerVariables>({});

  // Track focused field for variable insertion
  const [activeField, setActiveField] = useState<'subject' | 'body_html' | 'body_text' | null>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyHtmlRef = useRef<HTMLTextAreaElement>(null);
  const bodyTextRef = useRef<HTMLTextAreaElement>(null);

  // Error
  const [error, setError] = useState('');

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    setError('');
    try {
      const res = await emailAPI.getTemplates();
      setTemplates(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  // Fetch trigger variables
  const fetchTriggerVariables = useCallback(async () => {
    try {
      const res = await emailAPI.getTriggerVariables();
      setTriggerVariables(res.data);
    } catch {
      // non-critical
    }
  }, []);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const res = await emailAPI.getLogs();
      setLogs(res.data);
    } catch {
      // non-critical
    } finally {
      setLoadingLogs(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
    fetchTriggerVariables();
  }, [fetchTemplates, fetchTriggerVariables]);

  // Open editor
  const openEditor = useCallback((template: EmailTemplate) => {
    setSelectedTemplate(template);
    setForm({
      subject: template.subject,
      body_html: template.body_html,
      body_text: template.body_text,
      is_active: template.is_active,
      from_name: template.from_name || '',
      reply_to: template.reply_to || '',
    });
    setSaveError('');
    setSaveSuccess('');
    setShowEditor(true);
    setShowPreview(false);
    setPreviewHtml('');
  }, []);

  const closeEditor = useCallback(() => {
    setShowEditor(false);
    setSelectedTemplate(null);
    setForm(initialForm);
    setSaveError('');
    setSaveSuccess('');
    setShowPreview(false);
    setPreviewHtml('');
  }, []);

  // Update form field
  const updateField = useCallback((field: keyof TemplateForm, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setSaveSuccess('');
  }, []);

  // Save template
  const handleSave = useCallback(async () => {
    if (!selectedTemplate) return;
    setSaving(true);
    setSaveError('');
    setSaveSuccess('');
    try {
      await emailAPI.updateTemplate(selectedTemplate.id, form);
      setSaveSuccess('Template saved successfully!');
      fetchTemplates();
    } catch (err: any) {
      setSaveError(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  }, [selectedTemplate, form, fetchTemplates]);

  // Preview template
  const handlePreview = useCallback(async () => {
    if (!selectedTemplate) return;
    setLoadingPreview(true);
    try {
      // Save first, then preview
      await emailAPI.updateTemplate(selectedTemplate.id, form);
      const res = await emailAPI.previewTemplate(selectedTemplate.id);
      setPreviewHtml(res.data.html || res.data.preview || '');
      setShowPreview(true);
      fetchTemplates();
    } catch (err: any) {
      setSaveError(err.response?.data?.detail || 'Failed to preview template');
    } finally {
      setLoadingPreview(false);
    }
  }, [selectedTemplate, form, fetchTemplates]);

  // Send test email
  const handleSendTest = useCallback(async () => {
    if (!selectedTemplate || !testEmail) return;
    setSendingTest(true);
    setTestError('');
    setTestSuccess('');
    try {
      await emailAPI.sendTestEmail(selectedTemplate.id, testEmail);
      setTestSuccess(`Test email sent to ${testEmail}`);
      setTestEmail('');
    } catch (err: any) {
      setTestError(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  }, [selectedTemplate, testEmail]);

  // Insert variable into focused field
  const insertVariable = useCallback((variable: string) => {
    const tag = `{{${variable}}}`;
    const insertIntoRef = (
      ref: React.RefObject<HTMLInputElement | HTMLTextAreaElement>,
      field: keyof TemplateForm
    ) => {
      const el = ref.current;
      if (el) {
        const start = el.selectionStart ?? el.value.length;
        const end = el.selectionEnd ?? el.value.length;
        const newValue = el.value.substring(0, start) + tag + el.value.substring(end);
        setForm(prev => ({ ...prev, [field]: newValue }));
        // Restore cursor position after React re-renders
        setTimeout(() => {
          el.focus();
          el.setSelectionRange(start + tag.length, start + tag.length);
        }, 0);
      }
    };

    if (activeField === 'subject' && subjectRef.current) {
      insertIntoRef(subjectRef as React.RefObject<HTMLInputElement>, 'subject');
    } else if (activeField === 'body_html' && bodyHtmlRef.current) {
      insertIntoRef(bodyHtmlRef as React.RefObject<HTMLTextAreaElement>, 'body_html');
    } else if (activeField === 'body_text' && bodyTextRef.current) {
      insertIntoRef(bodyTextRef as React.RefObject<HTMLTextAreaElement>, 'body_text');
    } else {
      // Default: append to subject
      setForm(prev => ({ ...prev, subject: prev.subject + tag }));
    }
  }, [activeField]);

  // Toggle logs
  const toggleLogs = useCallback(() => {
    if (!showLogs) {
      fetchLogs();
    }
    setShowLogs(prev => !prev);
  }, [showLogs, fetchLogs]);

  // Available variables for the selected template's trigger
  const availableVariables: string[] =
    selectedTemplate && triggerVariables[selectedTemplate.trigger]
      ? triggerVariables[selectedTemplate.trigger]
      : [];

  return {
    // Templates
    templates,
    loadingTemplates,
    error,

    // Editor
    selectedTemplate,
    showEditor,
    openEditor,
    closeEditor,
    form,
    updateField,
    saving,
    saveError,
    saveSuccess,
    handleSave,

    // Preview
    previewHtml,
    showPreview,
    setShowPreview,
    loadingPreview,
    handlePreview,

    // Test email
    testEmail,
    setTestEmail,
    showTestModal,
    setShowTestModal,
    sendingTest,
    testSuccess,
    setTestSuccess,
    testError,
    setTestError,
    handleSendTest,

    // Logs
    logs,
    loadingLogs,
    showLogs,
    toggleLogs,

    // Variables
    availableVariables,
    insertVariable,
    activeField,
    setActiveField,
    subjectRef,
    bodyHtmlRef,
    bodyTextRef,
  };
}
